import React, { useState, useEffect, useCallback } from 'react';
import { getAllLogs, getAttendanceUsers, getShifts, authorizeOvertime, deleteShift, updateAttendanceLog, adminMarkAttendance, createManualShift } from './attendanceApi';
import { format, differenceInMinutes, parseISO, getDay, isAfter, isBefore, isSameDay, startOfDay, addDays, subDays, addHours, set } from 'date-fns';
import { es } from 'date-fns/locale';
import { RefreshCw, User as UserIcon, Clock, Calendar, Banknote, TrendingUp, AlertCircle, Timer, UserX, Activity, History, ChevronUp, ChevronDown, Target, BarChart3, CheckCircle2, Trash2, Edit2, Check, X, XCircle, Filter, LogOut, PlusCircle, LogIn } from 'lucide-react';
import { WeeklyReport } from './WeeklyReport';
import { toast } from 'react-toastify';
import { Modal, Button, Form } from 'react-bootstrap';

// Sub-componente para editar la hora in-place
const EditableTime = ({ logId, timestamp, onUpdate, icon: Icon }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [newTime, setNewTime] = useState(timestamp ? format(new Date(timestamp), 'HH:mm') : '');

    const handleSave = async () => {
        if (!logId) return;
        try {
            // CORRECCIÓN: Usar 'set' de date-fns para modificar solo horas y minutos, 
            // manteniendo el resto (año, mes, día) intacto y eliminando segundos.
            const [hours, minutes] = newTime.split(':');
            const updatedDate = set(new Date(timestamp), {
                hours: parseInt(hours),
                minutes: parseInt(minutes),
                seconds: 0,
                milliseconds: 0
            });
            
            await updateAttendanceLog(logId, { timestamp: updatedDate.toISOString() });
            toast.success("Horario corregido");
            setIsEditing(false);
            onUpdate();
        } catch (error) {
            toast.error("Error al actualizar");
        }
    };

    if (isEditing) {
        return (
            <div className="flex items-center gap-1 animate-in fade-in zoom-in duration-200">
                <input 
                    type="time" 
                    value={newTime} 
                    onChange={(e) => setNewTime(e.target.value)}
                    className="bg-white border border-slate-300 rounded px-1 py-0.5 text-[10px] font-bold outline-none focus:border-blue-500"
                />
                <button onClick={handleSave} className="p-1 bg-emerald-500 text-white rounded hover:bg-emerald-600 transition-colors">
                    <Check size={10} />
                </button>
                <button onClick={() => setIsEditing(false)} className="p-1 bg-slate-200 text-slate-500 rounded hover:bg-slate-300 transition-colors">
                    <X size={10} />
                </button>
            </div>
        );
    }

    return (
        <span 
            className="flex items-center gap-1 cursor-pointer hover:text-blue-600 hover:bg-blue-50 px-1 rounded transition-all group/time"
            onClick={() => logId && setIsEditing(true)}
            title={logId ? "Haga clic para editar hora" : ""}
        >
            {Icon && <Icon size={10} className={timestamp ? 'text-emerald-500' : 'text-slate-300'} />}
            {timestamp ? format(new Date(timestamp), 'HH:mm') : '--:--'}
            {logId && <Edit2 size={8} className="opacity-0 group-hover/time:opacity-100 ml-1 text-blue-400" />}
        </span>
    );
};

export function AdminAttendance() {
  const [sessions, setSessions] = useState([]);
  const [users, setUsers] = useState([]); // Nuevo: Para el filtro de locutores
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(new Date());
  const [showFullHistory, setShowFullHistory] = useState(false);
  const [view, setView] = useState('dashboard');
  const [filterUserId, setFilterUserId] = useState(''); // Nuevo: ID del locutor a filtrar

  // NUEVO: Estados para Carga de Turno Manual
  const [showManualModal, setShowManualModal] = useState(false);
  const [manualData, setManualData] = useState({
      user_id: '',
      date: format(new Date(), 'yyyy-MM-dd'),
      start_time: '09:00',
      end_time: '18:00',
      note: ''
  });

  const EXTRA_RATE_NORMAL = 5000;
  const EXTRA_RATE_SUNDAY = 7500;
  const TOLERANCE_MINUTES = 15;

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(timer);
  }, []);

  const handleDeleteShift = async (shiftId) => {
      if (window.confirm("¿Estás seguro de que deseas eliminar este turno programado?")) {
          try {
              await deleteShift(shiftId);
              toast.success("Turno eliminado");
              fetchData();
          } catch (error) {
              toast.error("No se pudo eliminar el turno");
          }
      }
  };

  const fetchData = useCallback(async () => {
    const isTodayInternal = (date) => isSameDay(startOfDay(new Date(date)), startOfDay(now));
    setLoading(true);
    try {
      const [logs, uData, shifts] = await Promise.all([
        getAllLogs(), 
        getAttendanceUsers(),
        getShifts()
      ]);
      
      setUsers(uData); // CONECTADO: Actualizamos el estado global de usuarios
      const userMap = {};
      uData.forEach(u => userMap[u.id] = u.name || u.email); 

      const userLogs = {};
      logs.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
          .forEach(log => {
            if (!userLogs[log.user_id]) userLogs[log.user_id] = [];
            userLogs[log.user_id].push(log);
          });

      const processedSessions = [];
      const usedLogIds = new Set();

      // 1. Procesar Turnos Programados
      shifts.forEach(shift => {
        const shiftStart = parseISO(shift.start_time);
        const shiftEnd = parseISO(shift.end_time);
        
        let schedMin = differenceInMinutes(shiftEnd, shiftStart);
        let breakMin = 0;
        if (shift.breaks) {
          shift.breaks.forEach(b => {
            const bDur = differenceInMinutes(parseISO(b.end_time), parseISO(b.start_time));
            schedMin -= bDur;
            breakMin += bDur;
          });
        }

        const logsForUser = userLogs[shift.user_id] || [];
        // BUSQUEDA DE LOGS USANDO TIME ABSOLUTO
        const checkInLog = logsForUser.find(l => 
            l.type === 'in' && 
            !usedLogIds.has(l.id) &&
            Math.abs(new Date(l.timestamp).getTime() - shiftStart.getTime()) < 43200000 
        );

        let checkOutLog = null;
        if (checkInLog) {
            usedLogIds.add(checkInLog.id);
            checkOutLog = logsForUser.find(l => 
                l.type === 'out' && 
                !usedLogIds.has(l.id) &&
                new Date(l.timestamp).getTime() >= new Date(checkInLog.timestamp).getTime() &&
                (new Date(l.timestamp).getTime() - new Date(checkInLog.timestamp).getTime()) < 86400000
            );
            if (checkOutLog) usedLogIds.add(checkOutLog.id);
        }

        processedSessions.push({
          id: shift.id,
          userId: shift.user_id,
          userName: userMap[shift.user_id] || 'Desconocido',
          date: format(shiftStart, 'yyyy-MM-dd'),
          checkIn: checkInLog?.timestamp || null,
          checkInId: checkInLog?.id || null, 
          checkOut: checkOutLog?.timestamp || null,
          checkOutId: checkOutLog?.id || null,
          isAuthorized: checkOutLog?.is_overtime_authorized || false,
          totalMinutes: 0,
          scheduledMinutes: schedMin,
          breakMinutes: breakMin, // Colación total programada
          extraMinutes: 0,
          basePayment: shift.amount || 0,
          extraPayment: 0,
          estimatedPayment: 0,
          dayOfWeek: getDay(shiftStart),
          hasShift: true,
          scheduledStart: shift.start_time,
          scheduledEnd: shift.end_time,
          sortDate: shiftStart
        });
      });

      // 2. Marcaciones sin turno (Extras)
      logs.forEach(log => {
          if (log.type === 'in' && !usedLogIds.has(log.id)) {
              const checkOut = logs.find(l => l.type === 'out' && l.user_id === log.user_id && new Date(l.timestamp).getTime() > new Date(log.timestamp).getTime() && !usedLogIds.has(l.id));
              processedSessions.push({
                userId: log.user_id,
                userName: userMap[log.user_id] || 'Desconocido',
                date: format(new Date(log.timestamp), 'yyyy-MM-dd'),
                checkIn: log.timestamp,
                checkInId: log.id,
                checkOut: checkOut?.timestamp || null,
                checkOutId: checkOut?.id || null,
                isAuthorized: checkOut?.is_overtime_authorized || false,
                totalMinutes: 0,
                scheduledMinutes: 0,
                breakMinutes: 0,
                extraMinutes: 0,
                basePayment: 0,
                extraPayment: 0,
                estimatedPayment: 0,
                dayOfWeek: getDay(new Date(log.timestamp)),
                hasShift: false,
                scheduledStart: null,
                scheduledEnd: null,
                sortDate: new Date(log.timestamp)
              });
              usedLogIds.add(log.id);
              if (checkOut) usedLogIds.add(checkOut.id);
          }
      });

      // 3. Calcular Estados y Pagos
      processedSessions.forEach(s => {
          if (s.checkIn) {
              const checkInDate = new Date(s.checkIn);
              const endPoint = s.checkOut ? new Date(s.checkOut) : now;
              
              // CÁLCULO FÍSICO POR MILISEGUNDOS (Infalible contra Timezones)
              const diffMs = endPoint.getTime() - checkInDate.getTime();
              s.totalMinutes = Math.floor(diffMs / 60000);

              const startReference = s.scheduledStart ? parseISO(s.scheduledStart) : checkInDate;
              const lateMs = checkInDate.getTime() - startReference.getTime();
              const late = Math.floor(lateMs / 60000);

              if (s.checkOut) {
                  s.status = 'finished';
                  // Al finalizar el turno, sí descontamos la colación para el cálculo de extras real
                  const extra = s.hasShift ? Math.max(0, s.totalMinutes - s.scheduledMinutes - s.breakMinutes) : s.totalMinutes;
                  
                  if (extra > (s.hasShift ? TOLERANCE_MINUTES : 0)) {
                      const rate = s.dayOfWeek === 0 ? EXTRA_RATE_SUNDAY : EXTRA_RATE_NORMAL;
                      s.extraPayment = Math.round((extra / 60) * rate);
                      s.extraMinutes = extra;
                  }
                  s.estimatedPayment = s.basePayment + (s.isAuthorized ? s.extraPayment : 0);
              } else {
                  // --- TRABAJANDO: MOSTRAR TIEMPO BRUTO PARA QUE EL ADMIN NO SE CONFUNDA ---
                  const hoursElapsed = s.totalMinutes / 60;
                  const isOldShift = !isTodayInternal(s.sortDate) && isBefore(s.sortDate, startOfDay(now));

                  if (hoursElapsed > 14 || isOldShift) {
                      s.status = 'incomplete';
                      s.estimatedPayment = s.basePayment;
                  } else {
                      s.status = (s.hasShift && late > 5) ? 'late_arrived' : 'on_time';
                      s.estimatedPayment = s.basePayment;
                  }
              }
          } else if (s.hasShift) {
              const schedStart = parseISO(s.scheduledStart);
              s.estimatedPayment = s.basePayment;
              if (isBefore(schedStart, now)) {
                  if (isSameDay(schedStart, now)) {
                      const latePending = Math.floor((now.getTime() - schedStart.getTime()) / 60000);
                      s.status = latePending > 30 ? 'absent' : 'late_pending';
                  } else {
                      s.status = 'absent';
                  }
              } else {
                  s.status = 'future';
              }
          }
      });

      setSessions(processedSessions.sort((a, b) => b.sortDate.getTime() - a.sortDate.getTime()));
      
    } catch (error) { console.error(error); } finally { setLoading(false); }
  }, [now]);

  useEffect(() => { 
    if (view === 'dashboard') fetchData(); 
  }, [fetchData, view]);

  const handleToggleAuth = async (session) => {
    if (!session.checkOutId) {
      toast.info("El trabajador debe finalizar el turno para autorizar extras.");
      return;
    }
    try {
      await authorizeOvertime(session.checkOutId, !session.isAuthorized);
      toast.success(session.isAuthorized ? "Sobretiempo revocado" : "Sobretiempo autorizado");
      fetchData();
    } catch (error) {
      toast.error("Error al actualizar autorización");
    }
  };

  const handleRejectOvertime = async (session) => {
    if (!session.checkOutId) return;
    
    const targetTime = session.scheduledEnd || session.checkIn;
    const msg = session.scheduledEnd 
        ? "¿Rechazar horas extras? Se ajustará la salida a la hora programada."
        : "¿Rechazar este turno extra? Se ajustará la salida para que el pago sea $0.";

    if (!window.confirm(msg)) return;
    
    try {
        await updateAttendanceLog(session.checkOutId, { timestamp: typeof targetTime === 'string' ? targetTime : targetTime.toISOString() });
        toast.success("Sobretiempo rechazado");
        fetchData();
    } catch (error) {
        toast.error("Error al rechazar");
    }
  };

  const handleForceClose = async (session) => {
    if (!session.userId) return;
    
    let targetOutTime = session.scheduledEnd;
    let targetInTime = session.checkIn; // CONECTADO
    
    if (!targetOutTime) {
        // Es un turno EXTRA (sin programación)
        const checkInDate = new Date(targetInTime);
        targetOutTime = addHours(checkInDate, 8);
        if (isAfter(targetOutTime, now)) targetOutTime = now;
    }

    const outStr = format(new Date(targetOutTime), 'HH:mm');
    
    // MEJORA: Si entró temprano, ofrecer normalizar entrada también para evitar extras fantasma
    const checkInDate = new Date(targetInTime);
    const schedStartDate = session.scheduledStart ? new Date(session.scheduledStart) : null;
    const enteredEarly = schedStartDate && isBefore(checkInDate, schedStartDate);

    let normalizeEntry = false;
    if (enteredEarly) {
        normalizeEntry = window.confirm(`El trabajador entró temprano (${format(checkInDate, 'HH:mm')}). ¿Quieres normalizar el turno completo (Entrada ${format(schedStartDate, 'HH:mm')} y Salida ${outStr}) para que no generen extras?`);
    } else {
        if (!window.confirm(`¿Forzar el cierre de este turno? Se marcará la salida a las ${outStr}.`)) return;
    }
    
    try {
        // 1. Marcar la salida
        await adminMarkAttendance({
            user_id: session.userId,
            type: 'out',
            timestamp: typeof targetOutTime === 'string' ? targetOutTime : targetOutTime.toISOString(),
            note: "Cierre forzado por administrador"
        });

        // 2. Si se eligió normalizar, ajustar la entrada también
        if (normalizeEntry && session.checkInId && session.scheduledStart) {
            await updateAttendanceLog(session.checkInId, { timestamp: session.scheduledStart });
        }

        toast.success("Turno cerrado y normalizado");
        fetchData();
    } catch (error) {
        toast.error("Error al cerrar turno");
    }
  };

  const handleForceStart = async (session) => {
      if (!session.userId) return;
      
      const targetInTime = session.scheduledStart || new Date().toISOString();
      const inStr = format(new Date(targetInTime), 'HH:mm');

      if (!window.confirm(`¿Forzar el inicio de este turno? Se marcará la entrada a las ${inStr}.`)) return;

      try {
          await adminMarkAttendance({
              user_id: session.userId,
              type: 'in',
              timestamp: typeof targetInTime === 'string' ? targetInTime : targetInTime.toISOString(),
              note: "Inicio forzado por administrador"
          });

          toast.success("Turno iniciado forzosamente");
          fetchData();
      } catch (error) {
          toast.error("Error al iniciar turno");
      }
  };

  // NUEVO: Handler para Carga Manual de Turno
  const handleManualShiftSubmit = async (e) => {
      e.preventDefault();
      if (!manualData.user_id) {
          toast.error("Seleccione un trabajador");
          return;
      }

      try {
          const start = set(new Date(manualData.date), {
              hours: parseInt(manualData.start_time.split(':')[0]),
              minutes: parseInt(manualData.start_time.split(':')[1]),
              seconds: 0
          });
          const end = set(new Date(manualData.date), {
              hours: parseInt(manualData.end_time.split(':')[0]),
              minutes: parseInt(manualData.end_time.split(':')[1]),
              seconds: 0
          });

          if (isAfter(start, end)) {
              toast.error("La hora de inicio no puede ser posterior a la de término");
              return;
          }

          await createManualShift({
              user_id: parseInt(manualData.user_id),
              start_time: start.toISOString(),
              end_time: end.toISOString(),
              note: manualData.note
          });

          toast.success("Turno cargado correctamente");
          setShowManualModal(false);
          fetchData();
      } catch (error) {
          toast.error("Error al cargar turno manual");
      }
  };

  const formatDuration = (minutes) => {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
  };

  const displayedSessions = sessions.filter(s => {
    // 1. Aplicar Filtro de Locutor
    if (filterUserId && String(s.userId) !== String(filterUserId)) return false;

    // 2. Aplicar Filtro de Historial
    if (showFullHistory) {
        return isAfter(s.sortDate, subDays(now, 30));
    } else {
        const sessionDate = startOfDay(s.sortDate);
        const today = startOfDay(now);
        // MOSTRAR HOY, MAÑANA Y LOS PRÓXIMOS 4 DÍAS + TURNOS DE AYER QUE SIGAN ABIERTOS
        return isAfter(sessionDate, subDays(today, 1)) && isBefore(sessionDate, addDays(today, 4));
    }
  });

  const totalPaymentsInView = displayedSessions.reduce((acc, s) => acc + s.estimatedPayment, 0);

  if (view === 'report') {
    return <WeeklyReport onBack={() => setView('dashboard')} />;
  }

  const isToday = (date) => isSameDay(startOfDay(new Date(date)), startOfDay(now));

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between gap-4">
        <div className="flex gap-4">
          <div className="bg-white border border-slate-200 p-6 rounded-[2rem] shadow-sm">
            <p className="text-slate-400 text-[10px] font-black uppercase tracking-widest mb-1 flex items-center gap-2">
              <Banknote size={12} className="text-emerald-500" /> Pago Autorizado en Vista
            </p>
            <p className="text-4xl font-black text-slate-800 tracking-tighter">${totalPaymentsInView.toLocaleString()}</p>
          </div>
          
          <div className="bg-white border border-slate-200 p-6 rounded-[2rem] shadow-sm">
            <p className="text-slate-400 text-[10px] font-black uppercase tracking-widest mb-1 flex items-center gap-2">
              <Activity size={12} className="text-blue-500" /> Personal Activo
            </p>
            <p className="text-4xl font-black text-slate-800 tracking-tighter">
              {sessions.filter(s => s.checkIn && !s.checkOut && isSameDay(s.sortDate, now)).length}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 justify-end">
            {/* NUEVO: BOTÓN CARGA MANUAL */}
            <button 
                onClick={() => setShowManualModal(true)}
                className="flex items-center gap-2 px-6 py-4 rounded-2xl font-bold text-sm bg-blue-600 text-white hover:bg-blue-700 transition-all shadow-lg shadow-blue-200"
            >
                <PlusCircle size={18} /> Cargar Turno Manual
            </button>

            {/* NUEVO: FILTRO DE LOCUTOR */}
            <div className="relative group/filter">
                <Filter size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-hover/filter:text-blue-500 transition-colors" />
                <select 
                    value={filterUserId}
                    onChange={(e) => setFilterUserId(e.target.value)}
                    className="bg-white border border-slate-200 rounded-2xl pl-10 pr-4 py-4 text-xs font-bold outline-none focus:border-blue-500 shadow-sm transition-all min-w-[180px]"
                >
                    <option value="">Todos los Locutores</option>
                    {users.filter(u => u.role === 'locutor' || u.email?.toLowerCase() === 'maria.soledad.fernandezi@gmail.com' || u.role === 'worker').map(u => (
                        <option key={u.id} value={u.id}>{u.name || u.email}</option>
                    ))}
                </select>
            </div>

            <button onClick={() => setView('report')} className="flex items-center gap-2 px-6 py-4 rounded-2xl font-bold text-sm border bg-white border-slate-200 text-slate-600 hover:bg-slate-50 transition-all shadow-sm">
                <BarChart3 size={18} /> Reporte Semanal
            </button>
            <button onClick={() => setShowFullHistory(!showFullHistory)} className={`flex items-center gap-2 px-6 py-4 rounded-2xl font-bold text-sm border transition-all ${showFullHistory ? 'bg-slate-800 border-slate-800 text-white shadow-lg' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50 shadow-sm'}`}>
            <History size={18} /> {showFullHistory ? 'Ocultar Historial' : 'Ver Historial Reciente (30 días)'}
            </button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-[2.5rem] overflow-hidden shadow-xl">
        <div className="p-8 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
           <div>
             <h2 className="text-2xl font-black text-slate-800 flex items-center gap-3">
               <Target className="text-slate-400" size={24} /> 
               Operación
             </h2>
             <p className="text-slate-400 text-[10px] font-black uppercase tracking-widest mt-1">Monitoreo Cronológico Pro</p>
           </div>
           <button onClick={fetchData} className="p-4 bg-white hover:bg-slate-100 transition-colors rounded-2xl text-slate-400 border border-slate-200 shadow-sm">
             <RefreshCw size={24} className={loading ? 'animate-spin' : ''} />
           </button>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="text-slate-400 text-[10px] font-black uppercase tracking-[0.2em] bg-slate-50">
                <th className="px-10 py-8">Trabajador / Fecha</th>
                <th className="px-8 py-8">Estado</th>
                <th className="px-8 py-8">Registro Horario</th>
                <th className="px-8 py-8">Programación</th>
                <th className="px-10 py-8 text-right">Pago + Extras</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {displayedSessions.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-10 py-20 text-center text-slate-400 font-bold uppercase tracking-widest">
                    No hay registros recientes
                  </td>
                </tr>
              ) : displayedSessions.map((session, idx) => {
                const progress = session.scheduledMinutes > 0 ? Math.min(100, (session.totalMinutes / session.scheduledMinutes) * 100) : 0;
                const sessionIsToday = isToday(session.sortDate);

                return (
                  <tr key={idx} className={`transition-colors group ${sessionIsToday ? 'bg-blue-50/30' : 'hover:bg-slate-50/50'}`}>
                    <td className="px-10 py-6">
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center text-slate-400 group-hover:scale-110 transition-transform ${sessionIsToday ? 'bg-blue-100 text-blue-600' : 'bg-slate-100'}`}>
                          <UserIcon size={24} />
                        </div>
                        <div>
                          <p className="font-bold text-slate-700 m-0 text-lg leading-tight">{session.userName}</p>
                          <p className="text-[10px] text-slate-400 uppercase font-black mt-1 flex items-center gap-1">
                            <Calendar size={10} /> {format(session.sortDate, "EEE dd MMM", { locale: es })}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-8 py-6">
                      {session.status === 'absent' ? (
                        <div className="flex items-center gap-2 text-rose-600 font-black text-[10px] uppercase bg-rose-50 px-3 py-1.5 rounded-lg border border-rose-100 w-fit">
                          <UserX size={14} /> Ausente
                        </div>
                      ) : session.status === 'late_arrived' ? (
                        <div className="flex items-center gap-2 text-orange-800 font-black text-[10px] uppercase bg-orange-100 px-3 py-1.5 rounded-lg border border-orange-200 w-fit">
                          <AlertCircle size={14} /> Marcó Atrasado
                        </div>
                      ) : session.status === 'future' ? (
                        <div className="flex items-center gap-2 text-slate-400 font-black text-[10px] uppercase bg-slate-50 px-3 py-1.5 rounded-lg border border-slate-100 w-fit animate-pulse">
                          <Timer size={14} /> Programado
                        </div>
                      ) : session.status === 'finished' ? (
                        <div className="flex items-center gap-2 text-slate-600 font-black text-[10px] uppercase bg-slate-100 px-3 py-1.5 rounded-lg border border-slate-200 w-fit">
                          <CheckCircle2 size={14} /> Finalizado
                        </div>
                      ) : session.status === 'incomplete' ? (
                        <div className="flex items-center gap-2 text-slate-400 font-black text-[10px] uppercase bg-slate-50 px-3 py-1.5 rounded-lg border border-slate-100 w-fit italic">
                          <AlertCircle size={14} /> Sin Salida
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 text-emerald-600 font-black text-[10px] uppercase bg-emerald-50 px-3 py-1.5 rounded-lg border border-emerald-100 w-fit">
                          <Clock size={14} className="animate-pulse" /> Trabajando
                        </div>
                      )}
                    </td>
                    <td className="px-8 py-6">
                      <div className="space-y-3">
                        <div className="flex flex-col text-[10px] font-black text-slate-400 uppercase tracking-tighter gap-1">
                          {/* ENTRADA EDITABLE */}
                          <EditableTime 
                            logId={session.checkInId} 
                            timestamp={session.checkIn} 
                            icon={ChevronUp} 
                            onUpdate={fetchData} 
                          />
                          {/* SALIDA EDITABLE */}
                          <EditableTime 
                            logId={session.checkOutId} 
                            timestamp={session.checkOut} 
                            icon={ChevronDown} 
                            onUpdate={fetchData} 
                          />
                        </div>
                        <div className="w-44 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div 
                            className={`h-full rounded-full transition-all duration-1000 ${session.status.includes('late') ? 'bg-orange-400' : 'bg-slate-800'}`} 
                            style={{ width: `${progress}%` }}
                          ></div>
                        </div>
                      </div>
                    </td>
                    <td className="px-8 py-6">
                      <div className="flex items-center gap-2 text-slate-600 font-bold text-sm">
                        <Clock size={16} className="text-slate-300" />
                        <span>{session.hasShift ? formatDuration(session.scheduledMinutes) : 'Extra'}</span>
                      </div>
                      {session.totalMinutes > 0 && (
                        <p className="text-[10px] text-slate-400 font-black uppercase mt-1">Real: {formatDuration(session.totalMinutes)}</p>
                      )}
                    </td>
                    <td className="px-10 py-6 text-right">
                      <div className="flex items-center justify-end gap-4">
                        <div className="flex flex-col items-end">
                          <span className={`font-black text-3xl tracking-tighter leading-none ${session.isAuthorized ? 'text-slate-800' : 'text-slate-400'}`}>
                            ${session.estimatedPayment.toLocaleString()}
                          </span>
                          {session.extraPayment > 0 && (
                            <span className={`text-[10px] font-black uppercase mt-1 flex items-center gap-1 ${session.isAuthorized ? 'text-emerald-600' : 'text-slate-400'}`}>
                              <TrendingUp size={12} /> +${session.extraPayment.toLocaleString()} {session.isAuthorized ? 'autorizado' : 'por autorizar'}
                            </span>
                          )}
                        </div>
                        
                        <div className="flex gap-2">
                            {/* BOTÓN FORZAR INICIO (Para turnos programados que no han iniciado) */}
                            {!session.checkIn && session.hasShift && ['absent', 'late_pending', 'future'].includes(session.status) && (
                                <button 
                                    onClick={() => handleForceStart(session)}
                                    className="p-3 rounded-xl bg-blue-50 border border-blue-200 text-blue-600 hover:bg-blue-600 hover:text-white transition-all shadow-sm"
                                    title="Forzar Inicio de Turno (Marcar entrada a hora prog.)"
                                >
                                    <LogIn size={20} />
                                </button>
                            )}

                            {/* BOTÓN FORZAR CIERRE (Solo si no hay salida y es incompleto) */}
                            {!session.checkOut && session.checkIn && session.status === 'incomplete' && (
                                <button 
                                    onClick={() => handleForceClose(session)}
                                    className="p-3 rounded-xl bg-orange-50 border border-orange-200 text-orange-600 hover:bg-orange-600 hover:text-white transition-all shadow-sm"
                                    title="Forzar Cierre de Turno (Marcar salida a hora prog.)"
                                >
                                    <LogOut size={20} />
                                </button>
                            )}

                            {session.extraPayment > 0 && (
                              <>
                                <button 
                                  onClick={() => handleToggleAuth(session)}
                                  className={`p-3 rounded-xl transition-all border ${
                                    session.isAuthorized 
                                      ? 'bg-emerald-500 border-emerald-600 text-white shadow-lg shadow-emerald-200' 
                                      : 'bg-white border-slate-200 text-slate-300 hover:border-emerald-500 hover:text-emerald-500'
                                  }`}
                                  title={session.isAuthorized ? "Revocar autorización" : "Autorizar pago de extras"}
                                >
                                  <CheckCircle2 size={20} />
                                </button>
                                
                                {!session.isAuthorized && (
                                  <button 
                                    onClick={() => handleRejectOvertime(session)}
                                    className="p-3 rounded-xl bg-white border border-slate-200 text-slate-300 hover:text-rose-500 hover:border-rose-500 transition-all"
                                    title="Rechazar Horas Extras (Ajustar salida)"
                                  >
                                    <XCircle size={20} />
                                  </button>
                                )}
                              </>
                            )}
                            
                            {session.hasShift && (
                                <button 
                                    onClick={() => handleDeleteShift(session.id)}
                                    className="p-3 rounded-xl bg-white border border-slate-200 text-slate-300 hover:text-rose-500 hover:border-rose-200 transition-all"
                                    title="Eliminar turno programado"
                                >
                                    <Trash2 size={20} />
                                </button>
                            )}
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* NUEVO: MODAL TURNO MANUAL */}
      <Modal show={showManualModal} onHide={() => setShowManualModal(false)} centered className="attendance-modal">
          <Modal.Header closeButton className="border-0 pb-0">
              <Modal.Title className="text-2xl font-black text-slate-800 tracking-tighter">Cargar Turno Manual</Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-8">
              <Form onSubmit={handleManualShiftSubmit} className="space-y-6">
                  <Form.Group>
                      <Form.Label className="text-[10px] font-black uppercase tracking-widest text-slate-400">Trabajador</Form.Label>
                      <Form.Select 
                          required
                          value={manualData.user_id}
                          onChange={e => setManualData({...manualData, user_id: e.target.value})}
                          className="rounded-xl border-slate-200 py-3 text-sm font-bold"
                      >
                          <option value="">Seleccionar...</option>
                          {users.map(u => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
                      </Form.Select>
                  </Form.Group>

                  <Form.Group>
                      <Form.Label className="text-[10px] font-black uppercase tracking-widest text-slate-400">Fecha del Turno</Form.Label>
                      <Form.Control 
                          type="date"
                          required
                          value={manualData.date}
                          onChange={e => setManualData({...manualData, date: e.target.value})}
                          className="rounded-xl border-slate-200 py-3 text-sm font-bold"
                      />
                  </Form.Group>

                  <div className="grid grid-cols-2 gap-4">
                      <Form.Group>
                          <Form.Label className="text-[10px] font-black uppercase tracking-widest text-slate-400">Hora Entrada</Form.Label>
                          <Form.Control 
                              type="time"
                              required
                              value={manualData.start_time}
                              onChange={e => setManualData({...manualData, start_time: e.target.value})}
                              className="rounded-xl border-slate-200 py-3 text-sm font-bold"
                          />
                      </Form.Group>
                      <Form.Group>
                          <Form.Label className="text-[10px] font-black uppercase tracking-widest text-slate-400">Hora Salida</Form.Label>
                          <Form.Control 
                              type="time"
                              required
                              value={manualData.end_time}
                              onChange={e => setManualData({...manualData, end_time: e.target.value})}
                              className="rounded-xl border-slate-200 py-3 text-sm font-bold"
                          />
                      </Form.Group>
                  </div>

                  <Form.Group>
                      <Form.Label className="text-[10px] font-black uppercase tracking-widest text-slate-400">Observación (Opcional)</Form.Label>
                      <Form.Control 
                          as="textarea"
                          rows={2}
                          value={manualData.note}
                          onChange={e => setManualData({...manualData, note: e.target.value})}
                          placeholder="Ej: No pudo marcar por error de sistema..."
                          className="rounded-xl border-slate-200 py-3 text-sm font-bold"
                      />
                  </Form.Group>

                  <div className="flex gap-2 pt-4">
                      <Button variant="primary" type="submit" className="flex-1 rounded-xl py-3 font-black uppercase tracking-widest text-[10px]">
                          Confirmar Carga de Turno
                      </Button>
                      <Button variant="slate-100" onClick={() => setShowManualModal(false)} className="px-6 rounded-xl py-3 font-black uppercase tracking-widest text-[10px] bg-slate-100 text-slate-500">
                          Cancelar
                      </Button>
                  </div>
              </Form>
          </Modal.Body>
      </Modal>
    </div>
  );
}
